import { useCallback, useEffect, useState } from 'react'

import { withRetry } from './retry'
import type { BridgeHandlerMap, BridgeHandlerName } from './types'
import { BridgeValidationError, validateResponse } from './validators'

interface UseBridgeOptions {
  /** 브릿지 연결 대기 타임아웃 (ms). 이 시간 내에 Flutter가 브릿지를 주입하지 않으면 timeout 상태로 전환 */
  timeoutMs?: number
  /** callHandler 실패 시 재시도 횟수 (기본 2) */
  maxRetries?: number
  /** 재시도 간격 ms (기본 500) */
  retryDelayMs?: number
}

/**
 * 브릿지 연결 상태
 * - connecting: Flutter가 window.flutter_inappwebview를 아직 주입하지 않은 상태. 대기 중
 * - ready: 브릿지가 주입되어 callHandler 사용 가능
 * - timeout: timeoutMs 내에 브릿지가 주입되지 않음. 앱 외부(브라우저 직접 접속 등)일 가능성
 */
type BridgeStatus = 'connecting' | 'ready' | 'timeout'

const DEFAULT_TIMEOUT_MS = 5000

/**
 * Flutter InAppWebView 브릿지 통신 훅
 *
 * ## 통신 흐름
 * 1. Flutter 앱이 WebView를 생성하면 window.flutter_inappwebview 객체를 주입
 * 2. 주입 완료 시 'flutterInAppWebViewPlatformReady' 이벤트 발생
 * 3. 이 훅이 이벤트를 감지하여 status를 'ready'로 전환
 * 4. callHandler로 Flutter 네이티브 함수 호출 (Promise 기반 양방향 통신)
 *
 * ## 사용 예시
 * ```tsx
 * const { isReady, isTimeout, callHandler } = useBridge()
 *
 * if (isTimeout) return <ErrorScreen />
 * if (!isReady) return <Loading />
 *
 * const { token } = await callHandler('getToken')
 * ```
 */
export function useBridge(options?: UseBridgeOptions) {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, maxRetries, retryDelayMs } = options ?? {}

  const status = useBridgeStatus(timeoutMs)

  const callHandler = useCallHandler({ maxRetries, retryDelayMs })

  return { status, isReady: status === 'ready', isTimeout: status === 'timeout', callHandler }
}

// ─── 연결 상태 관리 ────────────────────────────────────────

/**
 * Flutter 브릿지 연결 상태를 관리하는 내부 훅
 *
 * Flutter가 WebView를 띄우면 window.flutter_inappwebview를 주입하고
 * 'flutterInAppWebViewPlatformReady' 이벤트를 발생시킴
 * → 이 이벤트를 감지하여 status를 'ready'로 전환
 * → timeoutMs 내에 이벤트가 안 오면 'timeout'으로 전환 (무한 로딩 방지)
 */
function useBridgeStatus(timeoutMs: number): BridgeStatus {
  // lazy initializer: 최초 렌더 시 브릿지 존재 여부를 한 번만 체크
  const [status, setStatus] = useState<BridgeStatus>(() =>
    window.flutter_inappwebview ? 'ready' : 'connecting',
  )

  useEffect(() => {
    if (window.flutter_inappwebview) {
      setStatus('ready')
      return
    }

    // 언마운트 후 비동기 콜백에서 setState 호출 방지
    let cancelled = false

    const handleReady = () => {
      if (cancelled) return
      clearTimeout(timer)
      setStatus('ready')
    }

    window.addEventListener('flutterInAppWebViewPlatformReady', handleReady)

    // 안전장치: timeoutMs 내에 브릿지가 안 오면 timeout 처리
    const timer = setTimeout(() => {
      if (cancelled) return
      setStatus('timeout')
    }, timeoutMs)

    return () => {
      cancelled = true
      window.removeEventListener('flutterInAppWebViewPlatformReady', handleReady)
      clearTimeout(timer)
    }
  }, [timeoutMs])

  return status
}

// ─── 핸들러 호출 ──────────────────────────────────────────

function tryParseJson(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

interface CallHandlerOptions {
  maxRetries?: number
  retryDelayMs?: number
}

/**
 * Flutter 네이티브 함수 호출 메서드를 반환하는 내부 훅
 *
 * 재시도(withRetry)와 런타임 타입 검증(validateResponse)을 조합하여
 * 안전한 브릿지 통신을 제공
 */
function useCallHandler(options?: CallHandlerOptions) {
  const { maxRetries, retryDelayMs } = options ?? {}

  return useCallback(
    async <T extends BridgeHandlerName>(
      name: T,
      ...args: BridgeHandlerMap[T]['args'] extends void ? [] : [BridgeHandlerMap[T]['args']]
    ): Promise<BridgeHandlerMap[T]['response']> => {
      const bridge = window.flutter_inappwebview
      if (!bridge) {
        throw new Error(`[Bridge] Not available. Cannot call "${name}"`)
      }

      return withRetry(
        async () => {
          const raw = await bridge.callHandler(name, ...args)
          console.log(`[Bridge] "${name}" raw response:`, typeof raw, raw)

          // void 응답(haptic, closeWebView 등)은 검증 스킵
          if (raw === undefined || raw === null) {
            return raw as BridgeHandlerMap[T]['response']
          }

          // Flutter가 Map을 JSON 문자열로 직렬화해 보내는 경우 파싱
          const result = typeof raw === 'string' ? tryParseJson(raw) : raw

          validateResponse(name, result)
          return result as BridgeHandlerMap[T]['response']
        },
        {
          maxRetries,
          delayMs: retryDelayMs,
          shouldRetry: (error) => !(error instanceof BridgeValidationError),
          formatError: (error, totalAttempts) =>
            `[Bridge] "${name}" failed after ${totalAttempts} attempts: ${error instanceof Error ? error.message : String(error)}`,
        },
      )
    },
    [maxRetries, retryDelayMs],
  )
}
